import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useExportWorkStatistics, useGetWorksStatistics, WorksStatistics, workStatisticsQuery } from 'hooks/work'
import { useEffect, useMemo, useState } from 'react'
import { WorkType } from 'types/work'

type Props = {
  workType: WorkType
  initialUserId: string
}

export const defaultDataUnit = 'day'
export const defaultYear = new Date().getFullYear()
export const defaultFrom = getFirstDayOfWeek()
export const defaultDays = 5

export function useWorkStatisticsState({ initialUserId, workType }: Props) {
  const today = new Date()
  const queryClient = useQueryClient()
  const [userId, setUserId] = useState(initialUserId)
  const [dataUnit, setDataUnit] = useState<'day' | 'month'>(defaultDataUnit)
  const [year, setYear] = useState(defaultYear)
  const [from, setFrom] = useState(defaultFrom)
  const [days, setDays] = useState(defaultDays)
  const [month, setMonth] = useState(dayjs().month())

  const workTypes =
    workType === WorkType.SEWING ? [WorkType.SEWING_DOUBLE_NEEDLE, WorkType.SEWING_LOCKSTITCH] : [workType]
  const workStatistics = useGetWorksStatistics(userId, { workTypes, dataUnit, year, from, days })

  async function prefetch({ workTypes, ...props }: WorksStatistics) {
    return await Promise.all(
      workTypes.map(
        async (workType) => await queryClient.prefetchQuery(workStatisticsQuery(userId, { workType, ...props }))
      )
    )
  }

  const [dateRange, setDateRange] = useState('week')

  useEffect(() => {
    prefetch({ workTypes, dataUnit, year, from: dayjs(from).add(7, 'day').format('YYYY-MM-DD'), days })
    prefetch({ workTypes, dataUnit, year, from: dayjs(from).subtract(7, 'day').format('YYYY-MM-DD'), days })
  }, [userId])

  const weeklabels = useMemo(() => getWeekLabels(new Date(from)), [from])
  const monthLabels = useMemo(() => getMonthLabels(), [])

  const labels = useMemo<Array<string>>(() => {
    if (dateRange === 'week') return weeklabels
    if (dateRange === 'month') return monthLabels
    if (dateRange === 'year') return yearLabels
    return []
  }, [dateRange, weeklabels, monthLabels, yearLabels])

  const { mutateAsync } = useExportWorkStatistics()

  const currentInterval =
    dateRange === 'week'
      ? `${dayjs(from).format('DD.MM.YY')} - ${dayjs(from)
          .add(days - 1, 'day')
          .format('DD.MM.YY')}`
      : dateRange === 'month'
        ? `${year} ${yearLabels[month]}`
        : year

  function onDateRangeChange(value: string) {
    setDateRange(value)
    setYear(today.getFullYear())
    if (value === 'week') {
      setDataUnit('day')
      setFrom(getFirstDayOfWeek())
      setDays(5)
    }
    if (value === 'month') {
      setDataUnit('day')
      setFrom(getFirstDayOfMonth())
      setMonth(dayjs().month())
      setDays(dayjs().daysInMonth())
    }
    if (value === 'year') {
      setDataUnit('month')
    }
  }

  function getNextValues(props: { from: string; days: number; year: number; month: number }) {
    let newFrom = props.from
    let newDays = props.days
    let newYear = props.year
    let newMonth = props.month

    if (dateRange === 'week') newFrom = dayjs(props.from).add(7, 'day').format('YYYY-MM-DD')
    else if (dateRange === 'month') {
      newFrom = dayjs(props.from).add(1, 'month').format('YYYY-MM-DD')
      newDays = dayjs(props.from).add(1, 'month').daysInMonth()
      if (props.month === 11) {
        newYear = props.year + 1
        newMonth = 0
      } else {
        newMonth = props.month + 1
      }
    } else if (dateRange === 'year') newYear = props.year + 1

    return { from: newFrom, days: newDays, year: newYear, month: newMonth }
  }

  function getPreviousValues(props: { from: string; days: number; year: number; month: number }) {
    let newFrom = props.from
    let newDays = props.days
    let newYear = props.year
    let newMonth = props.month

    if (dateRange === 'week') newFrom = dayjs(props.from).subtract(7, 'day').format('YYYY-MM-DD')
    else if (dateRange === 'month') {
      newFrom = dayjs(props.from).subtract(1, 'month').format('YYYY-MM-DD')
      newDays = dayjs(props.from).subtract(1, 'month').daysInMonth()
      if (props.month === 0) {
        newYear = props.year - 1
        newMonth = 11
      } else {
        newMonth = props.month - 1
      }
    } else if (dateRange === 'year') newYear = props.year - 1

    return { from: newFrom, days: newDays, year: newYear, month: newMonth }
  }

  async function next() {
    const nextValues = getNextValues({ from, days, year, month })

    setFrom(nextValues.from)
    setDays(nextValues.days)
    setYear(nextValues.year)
    setMonth(nextValues.month)

    const prefetchValues = getNextValues(nextValues)

    await prefetch({ ...prefetchValues, dataUnit, workTypes })
  }

  async function previous() {
    const previousValues = getPreviousValues({ from, days, year, month })

    setFrom(previousValues.from)
    setDays(previousValues.days)
    setYear(previousValues.year)
    setMonth(previousValues.month)

    const prefetchValues = getPreviousValues(previousValues)

    await prefetch({ ...prefetchValues, dataUnit, workTypes })
  }

  async function exportStatisticsToPdf(fileName: string) {
    await mutateAsync({
      userId,
      workType,
      dataUnit,
      from,
      days,
      year,
      fileName,
    })
  }

  return {
    next,
    previous,
    labels,
    workStatistics,
    year,
    setYear,
    from,
    setFrom,
    days,
    setDays,
    month,
    setMonth,
    dateRange,
    setDateRange,
    userId,
    setUserId,
    dataUnit,
    setDataUnit,
    onDateRangeChange,
    currentInterval,
    exportStatisticsToPdf,
  }
}
const yearLabels = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
]

function getFirstDayOfWeek(): string {
  const today = new Date()
  const dayOfWeek = today.getDay()

  // Get the most recent Monday
  const monday = new Date(today)
  monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7))

  const day = String(monday.getDate()).padStart(2, '0')
  const month = String(monday.getMonth() + 1).padStart(2, '0')
  const year = monday.getFullYear()

  return `${year}-${month}-${day}`
}

function getFirstDayOfMonth(): string {
  return dayjs().startOf('month').format('YYYY-MM-DD')
}

function getWeekLabels(startDate: Date): Array<string> {
  const daysOfWeek = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota']

  const dayOfWeek = startDate.getDay()

  // Get the most recent Monday
  const monday = new Date(startDate)
  monday.setDate(startDate.getDate() - ((dayOfWeek + 6) % 7))

  return Array.from({ length: 5 }).map((_, i) => {
    const date = new Date(monday)
    date.setDate(monday.getDate() + i)

    const dayName = daysOfWeek[date.getDay()]
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')

    return `${dayName} (${day}.${month})`
  })
}

function getMonthLabels(): Array<string> {
  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth()

  // Ustal liczbę dni w aktualnym miesiącu
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  return Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString())
}
